// Single place to load all shared config data from public/data/.
// Call init() before ReactDOM.createRoot() so data is available synchronously
// everywhere in the app.

let _metValues    = null;
let _gymSchedule  = null;
let _foods        = null;
let _languages    = null;
let _manifest     = null;

const BASE = '/data';
const LEGACY_PATHS = {
  metValues: `${BASE}/met-values.json`,
  gymSchedule: `${BASE}/gym-schedule.json`,
  foods: `${BASE}/foods.json`,
  languages: `${BASE}/language/manifest.json`,
  languageLocale: `${BASE}/language/{code}.json`,
  sportsIndex: `${BASE}/sports/index.json`,
  sportCategory: `${BASE}/sports/{id}/{id}.json`,
  leanCollection: `${BASE}/lean.json`,
  promos: `${BASE}/promos.json`,
  messages: `${BASE}/messages.json`,
};

export function dataPath(key, replacements = {}) {
  let value = _manifest?.datasets?.[key] ?? LEGACY_PATHS[key];
  if (!value) throw new Error(`Unknown public data key: ${key}`);
  for (const [name, replacement] of Object.entries(replacements)) {
    value = value.replaceAll(`{${name}}`, encodeURIComponent(replacement));
  }
  return value;
}

export async function init() {
  try {
    const registryRes = await fetch(`${BASE}/manifest.json`);
    if (registryRes.ok) _manifest = await registryRes.json();
  } catch (e) {
    console.warn('[dataService] Failed to load data manifest; using legacy paths:', e);
  }

  const datasets = await Promise.allSettled([
    fetchJson(dataPath('metValues')),
    fetchJson(dataPath('gymSchedule')),
    fetchJson(dataPath('foods')),
    fetchJson(dataPath('languages')),
  ]);
  const [met, schedule, foods, languages] = datasets;
  if (met.status === 'fulfilled') _metValues = met.value.values;
  if (schedule.status === 'fulfilled') _gymSchedule = schedule.value;
  if (foods.status === 'fulfilled') _foods = foods.value;
  if (languages.status === 'fulfilled') _languages = languages.value.available;

  datasets.forEach((result, index) => {
    if (result.status === 'rejected') {
      const names = ['MET values', 'gym schedule', 'foods', 'languages'];
      console.warn(`[dataService] Failed to load ${names[index]}:`, result.reason);
    }
  });
}

async function fetchJson(path) {
  const response = await fetch(path);
  if (!response.ok) throw new Error(`${path} returned HTTP ${response.status}`);
  return response.json();
}

export const getMetValues    = () => _metValues;
export function setCustomMetValue(id, met) {
  if (!_metValues) _metValues = {};
  _metValues[id] = met;
}
export const getGymSchedule  = () => _gymSchedule;
export const getFoodsData    = () => _foods;
export const getLanguages    = () => _languages;
export const getDataManifest = () => _manifest;
export const getSupportedLngs = () =>
  _languages ? _languages.map(l => l.code) : ['en'];
